import { useMemo } from "react";
import { useGestaoTreinamentosCadastroViewModel } from "../../viewModels/gestao_treinamentos_cadastro_view_model";
import { GenerateTreinamentosColumnsDefinition } from "./columns/generate_treinamento";
import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendIcon } from "../../../../../common/components/legend_icon";
import { LegendItemComponent } from "../../../../../common/components/legend_item_component";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { FuncionarioTreinamento } from "../../../domain/models/funcionario_treinamento";
import { BackButton } from "../../../../../common/components/back_button";
import { CadastroTreinamentoModal } from "./components/cadastro_treinamento_modal";
import { AvaliarTreinamentoModal } from "./components/avaliar_treinamento_modal";
import { RelatorioTreinamentoModal } from "./components/relatorio_treinamento_modal";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";
import { HeaderStatus } from "./components/header_status";

import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * GestaoTreinamentosCadastro
 */

export const GestaoTreinamentosCadastro = () => {
  const viewModel = useGestaoTreinamentosCadastroViewModel();

  const treinamentosColumnsDefinition = useMemo(() => {
    return GenerateTreinamentosColumnsDefinition({
      onAvaliarTreinamento: (id) => viewModel.handleOpenAvaliarFuncionarioTreinamentoModal(id, true),
      onExportar: (id) => viewModel.handleRelatorioFuncionarioTreinamentoOpenState(id, true),
      onEditar: (id) => viewModel.handleOpenCadastroFuncionarioTreinamentoModal(id, true),
      onDeletar: (id) => viewModel.handleOpenDeletarFuncionarioTreinamentoModal(id, true),
    });
  }, [viewModel]);

  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          leftSlot={
            <div className="w-full flex flex-row gap-2">
              {viewModel.funcionario && (
                <div className="w-full h-full flex flex-col justify-center text-black dark:text-white">
                  <span className="mx-1 font-bold text-md">{viewModel.funcionario.nome}</span>
                  <span className="mx-1 font-light text-xs">{viewModel.funcionario.cargo}</span>
                </div>
              )}
            </div>
          }
          centerSlot={
            <div className="w-full h-full flex flex-row justify-center items-center gap-2">
              {
                viewModel.funcionario &&
                <HeaderStatus
                  status={viewModel.status}
                />
              }
            </div>
          }
          rightSlot={
            <BackButton
              onClick={viewModel.handleBack}
            />
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="PAGE flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            RH
          </Link>
          <Typography className="text-primary-green">
            Gestão de Treinamentos
          </Typography>
        </Breadcrumbs>

        <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-3xl font-medium text-black dark:text-white">Gestão de Treinamentos</h1>
            <RefreshButton onClick={viewModel.handleReloadPage} />
          </div>

          <div className="flex flex-row gap-2"></div>
        </div>

        {
          viewModel.errorMessage
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReloadPage} />
              </div>
            ) : (
              <Stack spacing={0} className="mt-4">
                <div className="div flex mt-8 justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
                  <div className="flex gap-2 items-center w-full">
                    <h1 className="text-xl font-medium text-black dark:text-white">
                      Treinamentos
                    </h1>
                  </div>

                  <div className="flex w-[40rem] pb-8 w-full justify-end flex gap-2">
                    <PrimaryButton
                      onClick={() => viewModel.handleOpenCadastroFuncionarioTreinamentoModal(null, true)}>
                      Novo treinamento
                    </PrimaryButton>

                    <PrimaryButton
                      buttonColor='yellow'
                      className="whitespace-nowrap"
                      onClick={() => viewModel.handleAvaliarLoteTreinamentoOpenState(true)}
                      sx={{ paddingX: 3 }}>
                      Avaliar Selecionados
                    </PrimaryButton>

                    <PrimaryButton
                      buttonColor='red'
                      className="whitespace-nowrap"
                      onClick={() => viewModel.handleOpenDeletarFuncionarioTreinamentoLoteModal(true)}
                      sx={{ paddingX: 3 }}>
                      Excluir Selecionados
                    </PrimaryButton>

                  </div>
                </div>

                <div className="w-full flex flex-row justify-between items-end my-4">
                  <LegendComponent>
                    <LegendItemComponent
                      icon={<LegendIcon color='red' title='A' />}
                      label='Avaliação em Atraso' />

                    <LegendItemComponent
                      icon={<LegendIcon color='blue' title='D' />}
                      label='Dentro do Prazo' />

                    <LegendItemComponent
                      icon={<LegendIcon color='yellow' title='N' />}
                      label='Não Realizado' />

                    <LegendItemComponent
                      icon={<LegendIcon color='green' title='A' />}
                      label='Avaliados' />
                  </LegendComponent>
                </div>

                <StyledDataGrid
                  tableName="gestao_treinamentos_cadastro"
                  disableColumnMenu={false}
                  disableRowSelectionOnClick={true}
                  checkboxSelection={true}
                  getRowId={(row: FuncionarioTreinamento) => row.idFuncionarioTreinamento}
                  columns={treinamentosColumnsDefinition}
                  loading={viewModel.loading}
                  rowSelectionModel={viewModel.rowsSelectionModel}
                  onRowSelectionModelChange={(gridSelectionModel) => viewModel.handleRowsSelectionChange(gridSelectionModel)}
                  rows={viewModel.funcionarioTreinamentos}
                  rowCount={viewModel.funcionarioTreinamentos.length}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationMode="server"
                  paginationModel={{
                    page: viewModel.pagination.page,
                    pageSize: viewModel.pagination.rowsPerPage,
                  }} />

                <TablePagination
                  component="div"
                  count={viewModel.pagination.totalItems}
                  page={viewModel.pagination.page - 1}
                  onPageChange={(_, p) => viewModel.handlePageChange(p)}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  rowsPerPage={viewModel.pagination.rowsPerPage}
                  onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

              </Stack>
            )
        }

        <CadastroTreinamentoModal
          open={viewModel.openCadastroFuncionarioTreinamentoModal}
          saving={viewModel.saving}
          selectedTreinamento={viewModel.selectedFuncionarioTreinamento}
          treinamentoLista={viewModel.filters.treinamentoLista ?? []}
          onSubmit={viewModel.handleSubmitTreinamento}
          onClose={viewModel.handleOpenCadastroFuncionarioTreinamentoModal} />

        <AvaliarTreinamentoModal
          open={viewModel.openAvaliarFuncionarioTreinamentoModal}
          saving={viewModel.saving}
          selectedTreinamento={viewModel.selectedFuncionarioTreinamento ?? null}
          onSubmit={viewModel.handleSubmitAvaliarTreinamento}
          onClose={viewModel.handleOpenAvaliarFuncionarioTreinamentoModal} />

        <RelatorioTreinamentoModal
          open={viewModel.openRelatorioFuncionarioTreinamentoModal}
          selectedFuncionarioTreinamento={viewModel.selectedFuncionarioTreinamento ?? null}
          funcionarioAssinaturas={viewModel.filters.funcionarioAssinaturas ?? []}
          onSubmit={viewModel.handleSubmitRelatorioFuncionarioTreinamento}
          onClose={viewModel.handleRelatorioFuncionarioTreinamentoOpenState} />

        <DeleteDialog
          open={viewModel.openDeletarFuncionarioTreinamentoModal}
          saving={viewModel.saving}
          onClose={() => viewModel.handleOpenDeletarFuncionarioTreinamentoModal(null, false)}
          title={`Você realmente deseja excluir o treinamento selecionado?`}
          onDelete={() => viewModel.handleConfirmarDeletarFuncionarioTreinamento()} />

        <DeleteDialog
          open={viewModel.openDeletarFuncionarioTreinamentoLoteModal}
          saving={viewModel.saving}
          onClose={() => viewModel.handleOpenDeletarFuncionarioTreinamentoLoteModal(false)}
          title={`Você realmente deseja excluir os treinamentos selecionados?`}
          onDelete={() => viewModel.handleConfirmarDeletarFuncionarioTreinamentosLote()} />

        <ReportViewerModal
          reportViewerState={viewModel.reportViewerState}
        />

      </div>
    </>
  );
}